import { useEffect, useRef, useState } from 'react'
import { ChevronDown, ChevronRight, PlugZap, Radar, Server, ShieldAlert, type LucideIcon } from 'lucide-react'
import { Link } from '../router.js'
import { apiGet } from '../admin/primitives.js'
import { Floating } from './floating.js'
import { ECHECS_AVANT_MUET, depuisLe } from './a-voir-modele.js'

/**
 * « À VOIR » — l'indicateur unique de l'en-tête (04/11/2026).
 *
 * Il remplace « 2/2 services à jour », le badge « Mode sûr » et le bloc
 * « État de Gestion » du menu. Vert, « Tout fonctionne » ; orange (rouge si
 * une panne), « N à voir ». Au clic, la liste de ce qui demande une action,
 * chaque ligne menant à l'écran où on la règle. La liste vient du serveur,
 * déjà filtrée par les droits du compte (GET /api/v1/a-voir) : un compte
 * qui n'a aucun de ces droits voit « Tout fonctionne », sans détail.
 */

interface Ligne { cle: string; titre: string; detail: string; gravite: 'panne' | 'attention'; lien: string; depuis: string | null }
interface Etat { lignes: Ligne[]; connecteurs_ok: number; connecteurs_suivis: number; sonde: 'ok' | 'indisponible' | 'non_configuree' }

const RAFRAICHIR_MS = 60_000

/** L'icône d'une ligne, d'après sa nature (préfixe de sa clé). */
function iconeDe(cle: string): LucideIcon {
  if (cle.startsWith('connecteur:')) return PlugZap
  if (cle.startsWith('infra:')) return Server
  if (cle === 'sonde') return Radar
  return ShieldAlert
}

/** « 2 pannes · 1 à surveiller » */
function resume(lignes: Ligne[]): string {
  const p = lignes.filter((l) => l.gravite === 'panne').length
  const a = lignes.length - p
  return [p ? `${p} panne${p > 1 ? 's' : ''}` : null, a ? `${a} à surveiller` : null].filter(Boolean).join(' · ')
}

export function AVoir() {
  const [etat, setEtat] = useState<Etat | null>(null)
  const [ouvert, setOuvert] = useState(false)
  const [echecs, setEchecs] = useState(0)
  const ancre = useRef<HTMLButtonElement>(null)
  const pop = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let vivant = true
    const charger = () => {
      apiGet<Etat>('/api/v1/a-voir')
        .then((e) => {
          if (!vivant) return
          if (Array.isArray(e?.lignes)) { setEtat(e); setEchecs(0) } else setEchecs((k) => k + 1)
        })
        // Gestion ne répond pas : jamais « Tout fonctionne » sur un état qu'on ne sait plus lire.
        .catch(() => { if (vivant) setEchecs((k) => k + 1) })
    }
    charger()
    const t = setInterval(charger, RAFRAICHIR_MS)
    return () => { vivant = false; clearInterval(t) }
  }, [])

  // Au clavier comme à la souris : le focus entre dans la fenêtre, et revient au bouton à la fermeture.
  useEffect(() => {
    if (!ouvert) return
    const id = requestAnimationFrame(() => {
      const cible = pop.current?.querySelector<HTMLElement>('.lc-avoirpop__ligne') ?? pop.current
      cible?.focus()
    })
    return () => cancelAnimationFrame(id)
  }, [ouvert])
  const fermer = () => {
    setOuvert(false)
    const a = document.activeElement
    if (!a || a === document.body || pop.current?.contains(a)) ancre.current?.focus()
  }

  const muet = echecs >= ECHECS_AVANT_MUET
  if (!etat && !muet) return null
  const n = muet ? 0 : etat!.lignes.length
  const panne = !muet && etat!.lignes.some((l) => l.gravite === 'panne')
  const ton = muet ? 'attention' : n === 0 ? 'ok' : panne ? 'panne' : 'attention'

  return (
    <>
      <button
        type="button" ref={ancre}
        className={`lc-avoir lc-avoir--${ton}`}
        aria-haspopup="dialog" aria-expanded={ouvert}
        aria-label={muet ? 'État indisponible' : n === 0 ? 'Tout fonctionne' : `${n} point${n > 1 ? 's' : ''} à voir`}
        onClick={() => setOuvert((v) => !v)}
      >
        <span className={`lc-avoir__point${panne ? ' lc-avoir__point--pulse' : ''}`} aria-hidden="true" />
        {/* Le nombre reste visible au téléphone : l'état ne se dit jamais par la couleur seule. */}
        {n > 0 ? <span className="lc-avoir__n">{n}</span> : null}
        <span className="lc-avoir__texte">{muet ? 'État indisponible' : n === 0 ? 'Tout fonctionne' : 'à voir'}</span>
        <ChevronDown size={14} aria-hidden="true" className="lc-avoir__chev" />
      </button>

      <Floating
        open={ouvert} anchorRef={ancre} placement="bottom-end"
        className="lc-avoirpop" role="dialog" ariaLabel="À voir"
        onOutside={fermer}
      >
        <div ref={pop} tabIndex={-1} className="lc-avoirpop__corps-fenetre">
        <div className="lc-avoirpop__tete">
          <span className="lc-avoirpop__h">{muet ? 'État indisponible' : n === 0 ? 'Tout fonctionne' : 'À voir'}</span>
          {!muet && n > 0 ? <span className={`lc-avoirpop__compte lc-avoirpop__compte--${panne ? 'panne' : 'attention'}`}>{resume(etat!.lignes)}</span> : null}
        </div>
        {muet ? (
          <p className="lc-avoirpop__calme">Gestion ne répond pas : le dernier état connu n’est plus garanti.</p>
        ) : n === 0 ? (
          <p className="lc-avoirpop__calme">Rien ne demande d’action pour l’instant.</p>
        ) : (
          <ul className="lc-avoirpop__liste">
            {etat!.lignes.map((l, i) => {
              const Icone = iconeDe(l.cle)
              const depuis = depuisLe(l.depuis)
              return (
              <li key={l.cle} style={{ animationDelay: `${60 + i * 50}ms` }} className="lc-avoirpop__item">
                <Link href={l.lien} className={`lc-avoirpop__ligne lc-avoirpop__ligne--${l.gravite}`} onClick={() => setOuvert(false)}>
                  <span className="lc-avoirpop__tuile" aria-hidden="true"><Icone size={18} strokeWidth={1.9} /></span>
                  <span className="lc-avoirpop__corps">
                    <span className="lc-avoirpop__titre">{l.titre}{depuis ? <span className="lc-avoirpop__depuis">{depuis}</span> : null}</span>
                    <span className="lc-avoirpop__detail">{l.detail}</span>
                  </span>
                  <ChevronRight size={16} aria-hidden="true" className="lc-avoirpop__va" />
                </Link>
              </li>
              )
            })}
          </ul>
        )}
        {!muet && etat && (etat.connecteurs_suivis > 0 || etat.sonde !== 'non_configuree') ? (
          <div className="lc-avoirpop__pied">
            <span className="lc-avoirpop__ok" aria-hidden="true" />
            {etat.connecteurs_suivis > 0 ? `${etat.connecteurs_ok} connecteur${etat.connecteurs_ok > 1 ? 's' : ''} sur ${etat.connecteurs_suivis} OK` : null}
            {etat.connecteurs_suivis > 0 && etat.sonde !== 'non_configuree' ? ' · ' : null}
            {etat.sonde === 'ok' ? 'Sonde OK' : etat.sonde === 'indisponible' ? 'Sonde muette' : null}
          </div>
        ) : null}
        </div>
      </Floating>
    </>
  )
}
